import React, { createContext, useContext } from "react";
import _ from 'lodash'

import { DesignFamily, DesignRegistration, DesignClass, DesignAgentLink, DesignCommodityLink, DesignFigure } from "./designs";
import { useCrud, useLinkedCrud } from "../BackendProvider";
import { design_family, design_registration, design_class, design_agent_link, design_commodity_link, design_figure } from "../data";
import { useAgents } from "../agents/AgentsProvider";
import { Agent } from "../agents/utils";

const DesignsContext = createContext({
    families: [] as DesignFamily[],
    familyById: {} as Record<number, DesignFamily>,
    familyByReference: {} as Record<string, DesignFamily>,
    postFamily: (family: DesignFamily) => Promise.resolve(family),
    deleteFamily: (family: DesignFamily) => Promise.resolve({}),

    registrations: [] as DesignRegistration[],
    registrationById: {} as Record<number, DesignRegistration>,
    registrationByReference: {} as Record<string, DesignRegistration>,
    registrationsByFamilyId: {} as Record<number, DesignRegistration[]>,
    postRegistration: (registration: DesignRegistration) => Promise.resolve(registration),
    deleteRegistration: (registration: DesignRegistration) => Promise.resolve({}),

    designClasses: [] as DesignClass[],
    designClassesByRegistrationId: {} as Record<number, DesignClass[]>,
    postDesignClass: (designClass: DesignClass) => Promise.resolve(designClass) as Promise<DesignClass | DesignClass[]>,
    deleteDesignClass: (designClass: DesignClass) => Promise.resolve({}),
    reloadDesignClasses: () => {},

    designAgentLinks: [] as DesignAgentLink[],
    postDesignAgentLink: (arg: (DesignAgentLink | DesignAgentLink[])) => Promise.resolve(arg),
    deleteDesignAgentLink: (arg: (DesignAgentLink | DesignAgentLink[])) => Promise.resolve({}),

    designersByRegistrationId: {} as Record<number, Agent[]>,
    ownersByRegistrationId: {} as Record<number, Agent[]>,
    contactsByRegistrationId: {} as Record<number, Agent[]>,
    applicantsByRegistrationId: {} as Record<number, Agent[]>,

    designProductMappings: [] as DesignCommodityLink[],
    postDesignProductMapping: (arg: (DesignCommodityLink | DesignCommodityLink[])) => Promise.resolve(arg),
    deleteDesignProductMapping: (arg: (DesignCommodityLink | DesignCommodityLink[])) => Promise.resolve({}),
    productMappingsByFamilyId: {} as Record<number, DesignCommodityLink[]>,

    figures: [] as DesignFigure[],
    figuresByRegistrationId: {} as Record<number, DesignFigure[]>,
    postFigure: (figure: DesignFigure) => Promise.resolve(figure),
    deleteFigure: (figure: DesignFigure) => Promise.resolve({}),
    reloadFigures: () => {},

    isLoading: false as boolean,
    reload: () => {},
});

export default function DesignsProvider({ children }: { children: React.ReactNode }) {
    const { agentById } = useAgents()

    const { data: families, postMutation: postFamily, deleteMutation: deleteFamily, isLoading: isLoadingFamilies, reload: reloadFamilies } = useCrud<DesignFamily>(design_family, f => f.designFamilyId)
    const { data: registrations, postMutation: postRegistration, deleteMutation: deleteRegistration, isLoading: isLoadingRegistrations, reload: reloadRegistrations } = useCrud<DesignRegistration>(design_registration, r => r.registrationId)

    const { data: designClasses, postMutation: postDesignClass, deleteMutation: deleteDesignClass, reload: reloadDesignClasses } = useCrud<DesignClass>(design_class, c => c.classId)

    const { data: designAgentLinks, postMutation: postDesignAgentLink, deleteMutation: deleteDesignAgentLink } =
        useLinkedCrud<DesignAgentLink>(
            design_agent_link,
            (a, b) => a.registrationId === b.registrationId && a.agentId === b.agentId && a.linkType === b.linkType
        )

    const { data: designProductMappings, postMutation: postDesignProductMapping, deleteMutation: deleteDesignProductMapping } =
        useLinkedCrud<DesignCommodityLink>(design_commodity_link, (a, b) => a.familyId === b.familyId && a.commodityId === b.commodityId)

    const { data: figures, postMutation: postFigure, deleteMutation: deleteFigure, reload: reloadFigures } = useCrud<DesignFigure>(design_figure, f => f.figureId)

    const familyById = _.keyBy(families, f => f.designFamilyId)
    const familyByReference = _.keyBy(families, f => f.reference)

    const registrationById = _.keyBy(registrations, r => r.registrationId)
    const registrationByReference = _.keyBy(registrations, r => r.reference)
    const registrationsByFamilyId = _.groupBy(registrations, r => r.familyId)

    const designClassesByRegistrationId = _.groupBy(designClasses, c => c.registrationId)

    const figuresByRegistrationId = _.groupBy(figures, f => f.registrationId)

    function linksById(
        links: DesignAgentLink[],
        agentById: Record<number, Agent>
    ): Record<number, Agent[]> {
        return _(links)
            .groupBy("registrationId")
            .mapValues(ls => ls.map(l => agentById[l.agentId]).filter(Boolean))
            .value()
    }

    const [designers, owners, contacts, applicants] = [
        designAgentLinks.filter(l => l.linkType === 'designer'),
        designAgentLinks.filter(l => l.linkType === 'owner'),
        designAgentLinks.filter(l => l.linkType === 'contact'),
        designAgentLinks.filter(l => l.linkType === 'applicant')
    ]

    const designersByRegistrationId = linksById(designers, agentById)
    const ownersByRegistrationId = linksById(owners, agentById)
    const contactsByRegistrationId = linksById(contacts, agentById)
    const applicantsByRegistrationId = linksById(applicants, agentById)

    const productMappingsByFamilyId = _.groupBy(designProductMappings, "familyId")

    const isLoading = isLoadingFamilies || isLoadingRegistrations

    function reload() {
        reloadFamilies()
        reloadRegistrations()
    }

    const value = {
        families,
        familyById,
        familyByReference,
        postFamily,
        deleteFamily,

        registrations,
        registrationById,
        registrationByReference,
        registrationsByFamilyId,
        postRegistration,
        deleteRegistration,

        designClasses,
        designClassesByRegistrationId,
        postDesignClass,
        deleteDesignClass,
        reloadDesignClasses,

        designAgentLinks,
        postDesignAgentLink,
        deleteDesignAgentLink,

        designersByRegistrationId,
        ownersByRegistrationId,
        contactsByRegistrationId,
        applicantsByRegistrationId,

        designProductMappings,
        postDesignProductMapping,
        deleteDesignProductMapping,
        productMappingsByFamilyId,

        figures,
        figuresByRegistrationId,
        postFigure,
        deleteFigure,
        reloadFigures,

        isLoading,
        reload,
    }

    return <DesignsContext.Provider {...{value}} >
        {children}
    </DesignsContext.Provider>;
}

export function useDesigns() {
    return React.useContext(DesignsContext)
}


const DesignRegistrationContext = createContext({
  registration: undefined as DesignRegistration,
  family: undefined as DesignFamily,
})

export function DesignRegistrationProvider({
  registration,
  family,
  children
}: {
  registration: DesignRegistration,
  family: DesignFamily,
  children: React.ReactNode
}) {
  return <DesignRegistrationContext.Provider value={{family, registration}}>{children}</DesignRegistrationContext.Provider>
}

export function useDesignRegistration() {
  return useContext(DesignRegistrationContext)
}
